import React from 'react';
import { useRecordContext } from 'react-admin';
import {
  Box, Grid, Paper, Typography, Stack, Divider, LinearProgress, Skeleton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RestoreIcon from '@mui/icons-material/Restore';
import PaidIcon from '@mui/icons-material/Paid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { DaysBand, daysBandLabel } from './labels';

// Cada número tem a explicação no ícone (i) ao lado do rótulo — pedido do Emílio (06/10):
// "deu pra entender nada". Definições: docs/CUSTOMER_RECOVERY_CAMPAIGN.md do pontuax-backend,
// seção "Métricas — definição única".
const SummaryRow: React.FC<{ label: string; hint: string; value: React.ReactNode; detail?: React.ReactNode }> = ({ label, hint, value, detail }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Tooltip title={hint} arrow enterTouchDelay={0} leaveTouchDelay={6000}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} aria-label={hint} />
      </Tooltip>
    </Stack>
    <Typography variant="body1" fontWeight={500}>{value}</Typography>
    {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
  </Box>
);

const SummaryCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
    <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
      {icon}
      <Typography variant="h6" fontWeight={600}>{title}</Typography>
    </Stack>
    <Divider sx={{ mb: 2 }} />
    <Stack spacing={1.5}>{children}</Stack>
  </Paper>
);

export const money = (v: number) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const pct = (v: number) => `${(Number(v || 0) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
export const count = (v: number) => Number(v || 0).toLocaleString('pt-BR');
export const times = (v: number | null | undefined) => (v != null ? `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}x` : '—');

// Pedido do Emílio (15/10): ver se a faixa de dias sem abastecer escolhida na criação faz
// sentido. Em geral quanto mais tempo parado, menos volta; a barra compara as faixas entre si.
const DaysBandsCard: React.FC<{ bands: DaysBand[]; audience: number }> = ({ bands, audience }) => {
  const maxRate = Math.max(...bands.map((b) => b.returnRate), 0.0001);
  return (
    <SummaryCard icon={<EventBusyIcon color="primary" />} title="Volta por tempo sem abastecer">
      <Typography variant="body2" color="text.secondary">
        Há quantos dias cada cliente estava sem abastecer quando entrou na campanha, e quantos voltaram em cada faixa.
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sem abastecer há</TableCell>
              <TableCell align="right">Clientes</TableCell>
              <TableCell align="right">Parte do público</TableCell>
              <TableCell align="right">Voltaram</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Taxa de volta</TableCell>
              <TableCell align="right">Com cupom</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bands.map((b) => (
              <TableRow key={`${b.kind}-${b.fromDays}`}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{daysBandLabel(b)}</span>
                    {b.kind === 'below_min' && (
                      <Tooltip
                        title="Abasteceram pouco antes da criação e entraram mesmo assim: a base do público é atualizada uma vez por dia, de madrugada."
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={6000}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{count(b.audience)}</TableCell>
                <TableCell align="right">{pct(audience ? b.audience / audience : 0)}</TableCell>
                <TableCell align="right">{count(b.returned)}</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LinearProgress
                      variant="determinate"
                      value={(b.returnRate / maxRate) * 100}
                      color="success"
                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'right' }}>{pct(b.returnRate)}</Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">{count(b.brought)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </SummaryCard>
  );
};

// Os quatro cartões (Público, Recuperação, Custo, Receita). Também desenham o "Total do período"
// do relatório de Recuperação de Clientes, que manda os mesmos campos sem `revenue30d` (undefined
// esconde o item) e sem `budgetUsedPct`. Vai dentro de um <Grid container>.
export const ResultCards: React.FC<{ m: any }> = ({ m }) => {
  const show30d = m.revenue30d !== undefined;
  const revenueSize = { xs: 12, sm: 6, md: show30d ? 3 : 4 };
  return (
    <>
      <Grid size={{ xs: 12, md: 4 }}>
        <SummaryCard icon={<PeopleAltIcon color="primary" />} title="Público">
          <SummaryRow
            label="Público"
            hint="Clientes que entraram na campanha no dia da criação. O público é fechado: ninguém entra depois."
            value={count(m.audience)}
          />
          <SummaryRow
            label="Ainda na campanha"
            hint="Clientes que ainda não voltaram a abastecer e seguem com o cupom no app."
            value={count(m.active)}
          />
          <SummaryRow
            label="Cupom venceu sem voltar"
            hint="Clientes cujo cupom venceu no fim do nível sem eles voltarem. Fica em 0 enquanto o primeiro nível não termina."
            value={count(m.expired)}
          />
        </SummaryCard>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <SummaryCard icon={<RestoreIcon color="success" />} title="Recuperação">
          <SummaryRow
            label="Voltaram a abastecer"
            hint="Clientes do público que abasteceram de novo depois da criação da campanha, com ou sem o cupom. Entre parênteses, a parte do público."
            value={`${count(m.recovered)} (${pct(m.recoveryRate)})`}
          />
          <SummaryRow
            label="Trazidos pela campanha (com cupom)"
            hint="Voltaram usando o cupom da campanha. É quem a campanha trouxe de forma comprovada."
            value={count(m.brought)}
          />
          <SummaryRow
            label="Voltaram sem cupom"
            hint="Voltaram, mas não usaram o cupom: abasteceram fora do app, usaram outro desconto ou não aplicaram o cupom. A campanha pode ter ajudado, mas não dá para comprovar."
            value={count(m.returnedWithoutCoupon)}
          />
        </SummaryCard>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <SummaryCard icon={<PaidIcon color="warning" />} title="Custo">
          <SummaryRow
            label="Custo total"
            hint="O que a campanha custou: nos níveis de desconto, o desconto dado nos abastecimentos que usaram o cupom (R$ por litro × litros); nos níveis de brinde, o custo do pacote de cada cliente que conquistou."
            value={money(m.cost)}
          />
          <SummaryRow
            label="Custo por cliente trazido"
            hint="Custo total dividido pelos clientes que voltaram usando o cupom."
            value={money(m.costPerBrought)}
          />
          {m.budgetUsedPct != null && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption" color="text.secondary">Orçamento usado</Typography>
                <Tooltip title="Parte do teto de gasto já usada. Ao chegar em 100%, a campanha encerra (conferido a cada 15 minutos)." arrow enterTouchDelay={0}>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </Stack>
              <LinearProgress variant="determinate" value={Math.min(100, m.budgetUsedPct * 100)} sx={{ mt: 0.5, mb: 0.5 }} />
              <Typography variant="body2">{pct(m.budgetUsedPct)}</Typography>
            </Box>
          )}
        </SummaryCard>
      </Grid>

      <Grid size={12}>
        <SummaryCard icon={<TrendingUpIcon color="primary" />} title="Receita">
          <Grid container spacing={2}>
            <Grid size={revenueSize}>
              <SummaryRow
                label="Receita da volta"
                hint="Valor do abastecimento de volta de todos que voltaram, com e sem cupom."
                value={money(m.returnRevenue)}
              />
            </Grid>
            <Grid size={revenueSize}>
              <SummaryRow
                label="Receita de quem usou o cupom"
                hint="Valor do abastecimento de volta só de quem voltou usando o cupom: o que a campanha trouxe de forma comprovada."
                value={m.broughtRevenue != null ? money(m.broughtRevenue) : '—'}
              />
            </Grid>
            {show30d && (
              <Grid size={revenueSize}>
                <SummaryRow
                  label="Abastecimentos seguintes (30 dias)"
                  hint="O que esses clientes abasteceram depois da volta, até 30 dias, sem contar o abastecimento da volta. Mostra se continuaram vindo. Para quem voltou há menos de 30 dias, ainda está acumulando."
                  value={m.revenue30d != null ? money(m.revenue30d) : '—'}
                />
              </Grid>
            )}
            <Grid size={revenueSize}>
              <SummaryRow
                label="Retorno por R$ de desconto"
                hint="Quanto quem voltou com o cupom abasteceu para cada R$ 1 de desconto dado. Acima de 5x é bom sinal."
                value={times(m.revenuePerReal)}
              />
            </Grid>
          </Grid>
        </SummaryCard>
      </Grid>
    </>
  );
};

const ResultTab: React.FC = () => {
  const record = useRecordContext();
  if (!record) return <Skeleton variant="rectangular" height={300} />;
  const m = record.metrics;
  if (!m) return <Typography color="text.secondary" p={2}>Sem métricas ainda.</Typography>;

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={2}>
        <ResultCards m={m} />

        {record.daysBands?.length > 0 && (
          <Grid size={12}>
            <DaysBandsCard bands={record.daysBands} audience={m.audience} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ResultTab;
